import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

const FOOTER_LINKS = {
  Services: [
    { label: 'How It Works',          path: '/how-it-works' },
    { label: 'Service Plans',         path: '/pricing' },
    { label: 'Certificate Listings',  path: '/certificate-listings' },
    { label: 'Member Dashboard',      path: '/dashboard' },
  ],
  Resources: [
    { label: 'Investment Guides',     path: '/resources?type=guide' },
    { label: 'Legal Templates',       path: '/resources?type=template' },
    { label: 'Market Reports',        path: '/resources?type=report' },
    { label: 'Webinars',              path: '/resources?type=webinar' },
  ],
  Company: [
    { label: 'About Us',              path: '/contact' },
    { label: 'How It Works',          path: '/how-it-works' },
    { label: 'Pricing Plans',         path: '/pricing' },
    { label: 'Contact Us',            path: '/contact' },
  ],
  Support: [
    { label: 'Member Login',          path: '/login' },
    { label: 'Create Account',        path: '/signup' },
    { label: 'Help Center',           path: '/resources' },
    { label: 'Privacy Policy',        path: '/contact' },
  ],
};

export default function Footer() {
  const [email, setEmail]         = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleNewsletter = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('email', email);
      await fetch('https://readdy.ai/api/form/d75cicavu4krcbn3bkag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch { setSubmitted(true); }
    finally { setSubmitting(false); }
  };

  return (
    <footer className="bg-[#0a0c13] border-t border-white/5 text-white">
      {/* Upper */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

          {/* Brand */}
          <div className="lg:col-span-1 flex flex-col gap-6">
            <Link to="/">
              <img
                src="https://public.readdy.ai/ai/img_res/e4baf86f-3d4f-4e4d-945f-ebc375a864af.png"
                alt="Foreclosure Recovery"
                className="h-12 w-auto object-contain"
              />
            </Link>
            <p className="text-white/40 text-sm leading-relaxed">
              Managed tax lien &amp; foreclosure certificate acquisition services nationwide across all 50 US states.
            </p>
            <div>
              <p className="text-white/30 text-xs mb-3">Get acquisition alerts</p>
              {submitted ? (
                <p className="text-gold text-sm font-medium">You&apos;re subscribed!</p>
              ) : (
                <form data-readdy-form onSubmit={handleNewsletter} className="flex gap-2">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="flex-1 px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/25 focus:outline-none focus:border-gold min-w-0"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg hover:bg-gold-light transition-colors flex-shrink-0"
                  >
                    <i className="ri-notification-3-line text-navy-dark text-base" />
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Links */}
          <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-8">
            {Object.entries(FOOTER_LINKS).map(([cat, links]) => (
              <div key={cat}>
                <h3 className="text-white/50 text-xs font-semibold uppercase tracking-widest mb-4">{cat}</h3>
                <ul className="flex flex-col gap-2.5">
                  {links.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.path}
                        className="text-sm text-white/35 hover:text-gold transition-colors duration-200"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Disclaimer bar */}
      <div className="border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-start gap-3">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-amber-400/70 text-xs" />
          </div>
          <p className="text-white/25 text-xs leading-relaxed">
            <span className="text-amber-400/80 font-semibold">Important:</span> All tax lien certificates and tax deeds are purchased directly through your county government office. Foreclosure Recovery provides research, data, and acquisition forms only — we are not a broker, dealer, or seller of tax certificates.
          </p>
        </div>
      </div>

      {/* Lower bar */}
      <div className="border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/25">© 2026 Foreclosure Recovery. All rights reserved.</p>
          <div className="flex items-center gap-3">
            {['linkedin-box', 'twitter-x', 'facebook-box', 'youtube'].map((icon) => (
              <a key={icon} href="#" rel="nofollow noopener noreferrer"
                className="w-7 h-7 flex items-center justify-center border border-white/10 rounded-md text-white/30 hover:text-gold hover:border-gold/40 transition-colors">
                <i className={`ri-${icon}-line text-xs`} />
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Link to="/contact" className="text-xs text-white/25 hover:text-white/60 transition-colors">Privacy Policy</Link>
            <span className="text-white/10">|</span>
            <Link to="/contact" className="text-xs text-white/25 hover:text-white/60 transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
